import { useContext, useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import {
    DocumentText,
    LampOn,
    MessageText,
    Share,
    Shop,
    VideoPlay,
} from "iconsax-react";
import DBButton from "../../../../component/global/DBButton";
import DBSelect from "../../../../component/global/DBSelect";
import DBSpinner from "../../../../component/global/DBSpinner";
import DBSwitch from "../../../../component/global/DBSwitch";
import { ToastContext } from "../../../../component/global/ToastContext";
import { FEEDBACK_PAGE_SECTIONS } from "../../../../helperData/ApiAddress";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import PrintPosterDesigner from "./print-poster/PrintPosterDesigner";
import { emptyPoster } from "./print-poster/constants";
import "./styles.scss";

const FORM_DISPLAY_SCROLL = "scroll";
const FORM_DISPLAY_STEP = "step";
const DEFAULT_SECTION = "feedback";

const SECTION_META = {
    details: DocumentText,
    feedback: MessageText,
    suggestion: LampOn,
    share: Share,
    contents: VideoPlay,
    market: Shop,
};

const FALLBACK_SECTIONS = [
    { code: "details", label: "مشخصات", custom_title: "", enabled: true },
    { code: "feedback", label: "پیام و نظر", custom_title: "", enabled: true },
    { code: "suggestion", label: "ایده نو", custom_title: "", enabled: true },
    { code: "share", label: "معرفی", custom_title: "", enabled: true },
    { code: "contents", label: "محتواها", custom_title: "", enabled: true },
    { code: "market", label: "سفارش", custom_title: "", enabled: true },
];
const CUSTOM_TITLE_MAX_LENGTH = 40;

function sectionDisplayLabel(section) {
    const custom = (section?.custom_title || "").trim();
    return custom || section?.label || "";
}

function mergeSections(loaded) {
    const byCode = Object.fromEntries(
        (loaded || []).map((section) => [section.code, section])
    );
    const known = FALLBACK_SECTIONS.map((fallback) => {
        const loadedSection = byCode[fallback.code];
        if (!loadedSection) {
            return { ...fallback };
        }
        return {
            ...fallback,
            enabled: loadedSection.enabled !== false,
            custom_title: loadedSection.custom_title || "",
        };
    });
    const extras = (loaded || [])
        .filter(
            (section) =>
                !FALLBACK_SECTIONS.some((item) => item.code === section.code)
        )
        .map((section) => ({
            ...section,
            custom_title: section.custom_title || "",
        }));
    return [...known, ...extras];
}

export default function FeedbackPageTab() {
    const toast = useContext(ToastContext);
    const [sections, setSections] = useState([]);
    const [formDisplayMode, setFormDisplayMode] = useState(FORM_DISPLAY_SCROLL);
    const [defaultSection, setDefaultSection] = useState(DEFAULT_SECTION);
    const [printPoster, setPrintPoster] = useState(emptyPoster());
    const [printTemplates, setPrintTemplates] = useState([]);
    const [company, setCompany] = useState(null);
    const [getSections, getSectionsLoader] = useRequest(
        FEEDBACK_PAGE_SECTIONS,
        "GET"
    );
    const [saveSections, saveSectionsLoader] = useRequest(
        FEEDBACK_PAGE_SECTIONS,
        "PUT"
    );

    const formDisplayOptions = [
        {
            value: FORM_DISPLAY_SCROLL,
            label: t("feedback.page.settings.form.display.scroll"),
        },
        {
            value: FORM_DISPLAY_STEP,
            label: t("feedback.page.settings.form.display.step"),
        },
    ];

    const enabledSections = useMemo(
        () => sections.filter((section) => section.enabled),
        [sections]
    );

    const defaultSectionOptions = useMemo(() => {
        const source = enabledSections.length > 0 ? enabledSections : sections;
        return source.map((section) => ({
            value: section.code,
            label: sectionDisplayLabel(section),
        }));
    }, [enabledSections, sections]);

    useEffect(() => {
        getSections()
            .then((response) => {
                const loadedSections =
                    response.sections?.length > 0
                        ? response.sections
                        : FALLBACK_SECTIONS;
                setSections(mergeSections(loadedSections));
                setFormDisplayMode(
                    response.form_display_mode || FORM_DISPLAY_SCROLL
                );
                setDefaultSection(
                    response.default_section || DEFAULT_SECTION
                );
                if (response.print_poster) {
                    setPrintPoster(response.print_poster);
                }
                setPrintTemplates(response.print_poster_templates || []);
                setCompany(response.company || null);
            })
            .catch(() => {
                setSections(FALLBACK_SECTIONS);
                toast(t("feedback.page.settings.load.error"), "danger");
            });
    }, []);

    useEffect(() => {
        if (defaultSectionOptions.length === 0) {
            return;
        }
        const isCurrentOptionValid = defaultSectionOptions.some(
            (option) => option.value === defaultSection
        );
        if (!isCurrentOptionValid) {
            const preferred = defaultSectionOptions.find(
                (option) => option.value === DEFAULT_SECTION
            );
            setDefaultSection(
                preferred?.value || defaultSectionOptions[0].value
            );
        }
    }, [defaultSection, defaultSectionOptions]);

    const applySettings = (response) => {
        setSections(mergeSections(response.sections || FALLBACK_SECTIONS));
        setFormDisplayMode(
            response.form_display_mode || FORM_DISPLAY_SCROLL
        );
        setDefaultSection(response.default_section || DEFAULT_SECTION);
        if (response.print_poster) {
            setPrintPoster(response.print_poster);
        }
        if (response.print_poster_templates) {
            setPrintTemplates(response.print_poster_templates);
        }
        if (response.company) {
            setCompany(response.company);
        }
    };

    const persist = ({
        nextSections = sections,
        nextFormDisplayMode = formDisplayMode,
        nextDefaultSection = defaultSection,
        nextPoster = printPoster,
    } = {}) => {
        const enabledCodes = nextSections
            .filter((section) => section.enabled)
            .map((section) => section.code);
        const resolvedDefault = enabledCodes.includes(nextDefaultSection)
            ? nextDefaultSection
            : enabledCodes.includes(DEFAULT_SECTION)
              ? DEFAULT_SECTION
              : enabledCodes[0] || DEFAULT_SECTION;

        return saveSections({
            sections: nextSections.map(({ code, enabled, custom_title }) => ({
                code,
                enabled: Boolean(enabled),
                custom_title: (custom_title || "").trim(),
            })),
            form_display_mode: nextFormDisplayMode,
            default_section: resolvedDefault,
            print_poster: nextPoster,
        })
            .then((response) => {
                applySettings(response);
                toast(t("feedback.page.settings.save.success"), "success");
            })
            .catch((error) => {
                toast(t("feedback.page.settings.save.error"), "danger");
                throw error;
            });
    };

    const setSectionEnabled = (code, enabled) => {
        const previous = sections;
        const next = sections.map((section) =>
            section.code === code
                ? { ...section, enabled: Boolean(enabled) }
                : section
        );
        setSections(next);
        persist({ nextSections: next }).catch(() => setSections(previous));
    };

    const setSectionCustomTitle = (code, customTitle) => {
        setSections((current) =>
            current.map((section) =>
                section.code === code
                    ? { ...section, custom_title: customTitle }
                    : section
            )
        );
    };

    const save = () => persist().catch(() => {});

    return (
        <div className="feedback-page-settings">
            <header className="feedback-page-hero">
                <p className="feedback-page-kicker">
                    {t("feedback.page.settings.kicker")}
                </p>
                <h1>{t("feedback.page.settings.title")}</h1>
                <p className="feedback-page-lead">
                    {t("feedback.page.settings.description")}
                </p>
            </header>
            {getSectionsLoader ? (
                <DBSpinner tip={t("loading")} />
            ) : (
                <>
                    <section className="feedback-page-panel">
                        <div className="feedback-page-panel__head">
                            <h2>{t("feedback.page.settings.tabs.title")}</h2>
                            <p>{t("feedback.page.settings.tabs.hint")}</p>
                        </div>
                        {enabledSections.length > 0 ? (
                            <div
                                className="feedback-page-preview"
                                aria-label={t(
                                    "feedback.page.settings.preview.label"
                                )}
                            >
                                {enabledSections.map((section) => (
                                    <span
                                        key={section.code}
                                        className={`feedback-page-preview__tab${
                                            section.code === defaultSection
                                                ? " is-start"
                                                : ""
                                        }`}
                                    >
                                        {sectionDisplayLabel(section)}
                                        {section.code === defaultSection ? (
                                            <em>
                                                {t(
                                                    "feedback.page.settings.start.badge"
                                                )}
                                            </em>
                                        ) : null}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                        <div className="feedback-page-section-grid">
                            {sections.map((section) => {
                                const Icon =
                                    SECTION_META[section.code] || DocumentText;
                                const isStart =
                                    section.enabled &&
                                    section.code === defaultSection;
                                return (
                                    <article
                                        key={section.code}
                                        className={`feedback-page-card${
                                            section.enabled ? " is-on" : ""
                                        }${isStart ? " is-start" : ""}`}
                                    >
                                        <div className="feedback-page-card__icon">
                                            <Icon size={20} variant="Linear" />
                                        </div>
                                        <div className="feedback-page-card__copy">
                                            <h3>{section.label}</h3>
                                            <p>
                                                {t(
                                                    `feedback.page.settings.section.${section.code}.hint`,
                                                    {
                                                        defaultValue: section.label,
                                                    }
                                                )}
                                            </p>
                                        </div>
                                        <div
                                            className="feedback-page-card__controls"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <DBSwitch
                                                checked={Boolean(section.enabled)}
                                                loading={saveSectionsLoader}
                                                onChange={(checked) =>
                                                    setSectionEnabled(
                                                        section.code,
                                                        checked
                                                    )
                                                }
                                                aria-label={section.label}
                                            />
                                            {section.enabled ? (
                                                <button
                                                    type="button"
                                                    className={`feedback-page-start${
                                                        isStart
                                                            ? " is-active"
                                                            : ""
                                                    }`}
                                                    onClick={() =>
                                                        setDefaultSection(
                                                            section.code
                                                        )
                                                    }
                                                >
                                                    {isStart
                                                        ? t(
                                                              "feedback.page.settings.start.active"
                                                          )
                                                        : t(
                                                              "feedback.page.settings.start.set"
                                                          )}
                                                </button>
                                            ) : null}
                                        </div>
                                        <label
                                            className="feedback-page-card__title"
                                            htmlFor={`feedback-section-title-${section.code}`}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <span>
                                                {t(
                                                    "feedback.page.settings.section.title"
                                                )}
                                            </span>
                                            <input
                                                id={`feedback-section-title-${section.code}`}
                                                type="text"
                                                value={section.custom_title || ""}
                                                placeholder={section.label}
                                                maxLength={CUSTOM_TITLE_MAX_LENGTH}
                                                autoComplete="off"
                                                dir="auto"
                                                onChange={(event) =>
                                                    setSectionCustomTitle(
                                                        section.code,
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </label>
                                    </article>
                                );
                            })}
                        </div>
                    </section>

                    <section className="feedback-page-panel">
                        <div className="feedback-page-panel__head">
                            <h2>
                                {t("feedback.page.settings.experience.title")}
                            </h2>
                            <p>
                                {t("feedback.page.settings.experience.hint")}
                            </p>
                        </div>
                        <div className="feedback-page-experience">
                            <div className="feedback-page-form-mode">
                                <label htmlFor="feedback-default-section">
                                    {t(
                                        "feedback.page.settings.default.section"
                                    )}
                                </label>
                                <DBSelect
                                    id="feedback-default-section"
                                    value={defaultSection}
                                    options={defaultSectionOptions}
                                    onChange={setDefaultSection}
                                    style={{ width: "100%" }}
                                    disabled={defaultSectionOptions.length === 0}
                                />
                                <p className="feedback-page-form-mode-hint">
                                    {t(
                                        "feedback.page.settings.default.section.hint"
                                    )}
                                </p>
                            </div>
                            <div className="feedback-page-mode-cards">
                                {formDisplayOptions.map((option) => (
                                    <button
                                        type="button"
                                        key={option.value}
                                        className={`feedback-page-mode-card${
                                            formDisplayMode === option.value
                                                ? " is-selected"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            setFormDisplayMode(option.value)
                                        }
                                    >
                                        <strong>{option.label}</strong>
                                        <span>
                                            {option.value === FORM_DISPLAY_STEP
                                                ? t(
                                                      "feedback.page.settings.form.display.step.hint"
                                                  )
                                                : t(
                                                      "feedback.page.settings.form.display.scroll.hint"
                                                  )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>

                    <PrintPosterDesigner
                        poster={printPoster}
                        templates={printTemplates}
                        company={company}
                        onChange={setPrintPoster}
                    />

                    <div className="feedback-page-save">
                        <DBButton
                            caption={t("save")}
                            onClick={save}
                            loading={saveSectionsLoader}
                            disabled={sections.length === 0}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
